import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Link,
  List,
  ListItem,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useAuthContext } from "../context/AuthContext";
import FaqEditor from "./editors/FaqEditor";
import TermsEditor from "./editors/TermsEditor";
import LgpdEditor from "./editors/LgpdEditor";
import AboutEditor from "./editors/AboutEditor";
import RichTextEditor from "./editors/RichTextEditor";
import TrainingKbEditor, {
  emptyTrainingKb,
  sanitizeTrainingKb,
} from "./editors/TrainingKbEditor";
import { getMetaPages, getMetaOauthUrl } from "../api/client";
import { MetaPagesGuidanceBlock } from "./meta/MetaPagesGuidanceBlock";

const TYPES = [
  { value: "como_comprar", label: "Como comprar", defaultSlug: "como-comprar" },
  { value: "pagamento", label: "Pagamento", defaultSlug: "pagamento" },
  { value: "entregas", label: "Entregas & prazos", defaultSlug: "entregas" },
  { value: "trocas", label: "Trocas e devoluções", defaultSlug: "trocas" },
  { value: "faq", label: "Perguntas frequentes", defaultSlug: "faq" },
  {
    value: "privacidade",
    label: "Declaração de Privacidade",
    defaultSlug: "declaracao-de-privacidade",
  },
  { value: "cookies", label: "Política de Cookies", defaultSlug: "cookies" },
  { value: "termos", label: "Termos de uso", defaultSlug: "terms" },
  { value: "lgpd", label: "LGPD", defaultSlug: "lgpd" },
  { value: "links_rodape", label: "Links do rodapé", defaultSlug: "links-rodape" },
  { value: "sugestoes_busca", label: "Sugestões de busca", defaultSlug: "sugestoes-busca" },
  { value: "segmentos", label: "Segmentos", defaultSlug: "segmentos" },
  { value: "sobre", label: "Quem somos", defaultSlug: "sobre" },
  { value: "home_seo", label: "Texto SEO da home", defaultSlug: "home-seo" },
  {
    value: "treinamento_plataforma",
    label: "Treinamento da plataforma",
    defaultSlug: "treinamento",
  },
  {
    value: "instagram_feed",
    label: "Últimos posts (Instagram)",
    defaultSlug: "instagram",
  },
];

const slugify = (s = "") =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/* ===== Shapes mínimos ===== */

const emptyFaq = {
  format: "faq.v1",
  lead: "",
  groups: [],
  contact: { text: "", note: "" },
  disclaimer: "",
};

const emptyTerms = {
  format: "terms.v1",
  intro: "",
  links: { privacy: { label: "", href: "", target: "_self" } },
  secoes: {
    introducao: { tipo: "text", texto: "" },
    aceitacao: { tipo: "text", texto: "" },
    conta: { tipo: "list", itens: [] },
    pagamentos: { tipo: "list", itens: [] },
    entregas: { tipo: "text", texto: "" },
    trocas: { tipo: "list", itens: [] },
    garantias: { tipo: "text", texto: "" },
    propriedade: { tipo: "text", texto: "" },
    uso: { tipo: "list", itens: [] },
    privacidade: { tipo: "text", texto: "" },
    comunicacoes: { tipo: "text", texto: "" },
    responsabilidade: { tipo: "text", texto: "" },
    indenizacao: { tipo: "text", texto: "" },
    modificacoes: { tipo: "text", texto: "" },
    rescisao: { tipo: "text", texto: "" },
    gerais: { tipo: "list", itens: [] },
    foro: { tipo: "text", texto: "", contato: { email: "", phone: "" } },
  },
  disclaimer: "",
};

const emptyLgpd = {
  format: "lgpd.v1",
  intro: "",
  controlador: "",
  contatos: [],
  bases_legais: [],
  finalidades: [],
  direitos: [],
  como_exercer_steps: [],
  compartilhamento: "",
  seguranca_retencao: "",
  anpd: "",
  links_refs: {
    privacy: { label: "", href: "", target: "_self" },
    cookies: { label: "", href: "", target: "_self" },
  },
  disclaimer: "",
};

const emptyAbout = {
  format: "about.v1",
  intro: "",
  hero: [],
  highlights: [],
  units: [],
  matriz_index: 0,
  disclaimer: "",
};

const emptyHomeSeo = {
  format: "home_seo.v1",
  h1: "",
  intro: "",
  sections: [
    { h2: "", body: "" },
    { h2: "", body: "" },
  ],
};

const emptyInstagramFeed = {
  format: "instagram_feed.v1",
  lead: "",
  instagram_business_account_id: "",
  facebook_page_id: "",
  page_access_token: "",
  ig_username: "",
  page_name: "",
};

/* ===== saneadores ===== */
const parseMaybe = (x) => {
  if (!x) return null;
  if (typeof x === "string") {
    try {
      return JSON.parse(x);
    } catch {
      return null;
    }
  }
  return x;
};
const arr = (x) => (Array.isArray(x) ? x : []);

/** ===== sanitize About ===== */
function sanitizeAbout(src) {
  const v = parseMaybe(src) || {};
  const img = (o) => ({
    src: String(o?.src || "").trim(),
    alt: String(o?.alt || "").trim(),
  });
  const hl = (o) => ({
    title: String(o?.title || "").trim(),
    desc: String(o?.desc || "").trim(),
  });
  const unit = (o) => ({
    slug: (o?.slug ? slugify(String(o.slug)) : slugify(o?.name || "")) || "",
    name: String(o?.name || "").trim(),
    address: String(o?.address || "").trim(),
    cityUF: String(o?.cityUF || "").trim(),
    phone: String(o?.phone || "").trim(),
    mapQuery: String(o?.mapQuery || "").trim(),
    image: String(o?.image || "").trim(),
  });

  const hero = arr(v.hero).map(img).filter((h) => h.src);
  const highlights = arr(v.highlights).map(hl).filter((h) => h.title || h.desc);
  const units = arr(v.units).map(unit).filter((u) => u.name && u.address);

  let matriz_index = Number.isFinite(Number(v.matriz_index))
    ? Math.max(0, Math.trunc(Number(v.matriz_index)))
    : 0;
  if (!units.length) matriz_index = 0;
  if (matriz_index >= units.length) matriz_index = 0;

  return {
    format: "about.v1",
    intro: v.intro || "",
    hero,
    highlights,
    units,
    matriz_index,
    disclaimer: v.disclaimer || "",
  };
}

function sanitizeFaq(src) {
  const v = parseMaybe(src) || {};
  return {
    format: "faq.v1",
    lead: v.lead || "",
    groups: arr(v.groups).map((g) => ({
      id: (g?.id || "").trim(),
      titulo: (g?.titulo || "").trim(),
      items: arr(g?.items).map((it) => ({
        pergunta: (it?.pergunta || "").trim(),
        resposta: (it?.resposta || "").trim(),
      })),
    })),
    contact: {
      text: (v?.contact?.text || "").trim(),
      note: (v?.contact?.note || "").trim(),
    },
    disclaimer: v.disclaimer || "",
  };
}

function sanitizeTerms(src) {
  const v = parseMaybe(src) || {};
  const s = v.secoes || {};

  const text = (sec) => {
    if (!sec) return { tipo: "text", texto: "" };
    const texto = typeof sec === "string" ? sec : sec.texto || "";
    return { tipo: "text", texto: String(texto || "").trim() };
  };

  const list = (sec) => {
    const base = Array.isArray(sec?.itens) ? sec.itens : Array.isArray(sec) ? sec : [];
    const itens = base.map((t) => String(t || "").trim()).filter(Boolean);
    return { tipo: "list", itens };
  };

  const contato = {
    email: String(s?.foro?.contato?.email || "").trim(),
    phone: String(s?.foro?.contato?.phone || "").trim(),
  };

  return {
    format: "terms.v1",
    intro: v.intro || "",
    links: {
      privacy: {
        label: (v?.links?.privacy?.label || "").trim(),
        href: (v?.links?.privacy?.href || "").trim(),
        target: v?.links?.privacy?.target === "_blank" ? "_blank" : "_self",
      },
    },
    secoes: {
      introducao: text(s?.introducao),
      aceitacao: text(s?.aceitacao),
      conta: list(s?.conta),
      pagamentos: list(s?.pagamentos),
      entregas: text(s?.entregas),
      trocas: list(s?.trocas),
      garantias: text(s?.garantias),
      propriedade: text(s?.propriedade),
      uso: list(s?.uso),
      privacidade: text(s?.privacidade),
      comunicacoes: text(s?.comunicacoes),
      responsabilidade: text(s?.responsabilidade),
      indenizacao: text(s?.indenizacao),
      modificacoes: text(s?.modificacoes),
      rescisao: text(s?.rescisao),
      gerais: list(s?.gerais),
      foro: { ...text(s?.foro), contato },
    },
    disclaimer: v.disclaimer || "",
  };
}

function sanitizeLgpd(src) {
  const v = parseMaybe(src) || {};
  const clean = (xs) => arr(xs).map((s) => String(s || "").trim()).filter(Boolean);
  return {
    format: "lgpd.v1",
    intro: v.intro || "",
    controlador: v.controlador || "",
    contatos: arr(v.contatos)
      .map((l) => ({
        label: (l?.label || "").trim(),
        href: (l?.href || "").trim(),
        target: l?.target === "_blank" ? "_blank" : "_self",
      }))
      .filter((l) => l.label || l.href),
    bases_legais: clean(v.bases_legais),
    finalidades: clean(v.finalidades),
    direitos: clean(v.direitos),
    como_exercer_steps: clean(v.como_exercer_steps),
    compartilhamento: v.compartilhamento || "",
    seguranca_retencao: v.seguranca_retencao || "",
    anpd: v.anpd || "",
    links_refs: {
      privacy: {
        label: (v?.links_refs?.privacy?.label || "").trim(),
        href: (v?.links_refs?.privacy?.href || "").trim(),
        target: v?.links_refs?.privacy?.target === "_blank" ? "_blank" : "_self",
      },
      cookies: {
        label: (v?.links_refs?.cookies?.label || "").trim(),
        href: (v?.links_refs?.cookies?.href || "").trim(),
        target: v?.links_refs?.cookies?.target === "_blank" ? "_blank" : "_self",
      },
    },
    disclaimer: v.disclaimer || "",
  };
}

function sanitizeHomeSeo(src) {
  const v = parseMaybe(src) || {};
  const sections = arr(v.sections).map((s) => ({
    h2: String(s?.h2 || "").trim(),
    body: String(s?.body || "").trim(),
  }));

  while (sections.length < 2) {
    sections.push({ h2: "", body: "" });
  }

  return {
    format: "home_seo.v1",
    h1: String(v.h1 || "").trim(),
    intro: String(v.intro || "").trim(),
    sections,
  };
}

function sanitizeInstagramFeed(src) {
  const v = parseMaybe(src) || {};
  return {
    format: "instagram_feed.v1",
    lead: String(v.lead || "").trim(),
    instagram_business_account_id: String(
      v.instagram_business_account_id || ""
    ).trim(),
    facebook_page_id: String(v.facebook_page_id || "").trim(),
    page_access_token: String(v.page_access_token || "").trim(),
    ig_username: String(v.ig_username || "").trim(),
    page_name: String(v.page_name || "").trim(),
  };
}

/* ===== Form ===== */
const AddContentForm = ({ contentData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const [form, setForm] = useState({
    ...contentData,
    id_usuario: contentData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    tipo: contentData?.tipo || "como_comprar",
    titulo: contentData?.titulo || "",
    slug: contentData?.slug || "",
    ordem: contentData?.ordem ?? "",
    status: contentData?.status == null ? 1 : Number(contentData.status),
    updated_at: contentData?.updated_at || "",
    auto_toc: !!contentData?.auto_toc,
    conteudo: contentData?.conteudo || "",
    conteudo_json: contentData?.conteudo_json || null,
    links: contentData?.links || [],
    faqs: contentData?.faqs || [],
    faq_groups: contentData?.faq_groups || [],
    lista: contentData?.lista || [],
  });

  // Editores estruturados
  const [faqData, setFaqData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "faq.v1"
      ? sanitizeFaq(contentData.conteudo_json)
      : emptyFaq
  );
  const [termsData, setTermsData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "terms.v1"
      ? sanitizeTerms(contentData.conteudo_json)
      : emptyTerms
  );
  const [lgpdData, setLgpdData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "lgpd.v1"
      ? sanitizeLgpd(contentData.conteudo_json)
      : emptyLgpd
  );
  const [aboutData, setAboutData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "about.v1"
      ? sanitizeAbout(contentData.conteudo_json)
      : emptyAbout
  );
  const [homeSeoData, setHomeSeoData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "home_seo.v1"
      ? sanitizeHomeSeo(contentData.conteudo_json)
      : emptyHomeSeo
  );
  const [trainingKbData, setTrainingKbData] = useState(() =>
    contentData?.conteudo_json && parseMaybe(contentData.conteudo_json)?.format === "training_kb.v1"
      ? sanitizeTrainingKb(contentData.conteudo_json)
      : emptyTrainingKb()
  );
  const [instagramFeedData, setInstagramFeedData] = useState(() =>
    contentData?.conteudo_json &&
    parseMaybe(contentData.conteudo_json)?.format === "instagram_feed.v1"
      ? sanitizeInstagramFeed(contentData.conteudo_json)
      : emptyInstagramFeed
  );

  const [metaAccessToken, setMetaAccessToken] = useState("");
  const [metaPages, setMetaPages] = useState([]);
  const [loadingMetaPages, setLoadingMetaPages] = useState(false);
  const [metaPagesError, setMetaPagesError] = useState(null);
  const [metaPagesGuidance, setMetaPagesGuidance] = useState(null);
  const [metaPagesRequestId, setMetaPagesRequestId] = useState(null);
  const [metaOauthError, setMetaOauthError] = useState(null);
  const [metaConnecting, setMetaConnecting] = useState(false);

  useEffect(() => {
    setForm((p) => ({
      ...p,
      ...contentData,
      id_usuario: contentData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      tipo: contentData?.tipo || "como_comprar",
      titulo: contentData?.titulo || "",
      slug: contentData?.slug || "",
      ordem: contentData?.ordem ?? "",
      status: contentData?.status == null ? 1 : Number(contentData.status),
      updated_at: contentData?.updated_at || "",
      auto_toc: !!contentData?.auto_toc,
      conteudo: contentData?.conteudo || "",
      conteudo_json: contentData?.conteudo_json || null,
      links: contentData?.links || [],
      faqs: contentData?.faqs || [],
      faq_groups: contentData?.faq_groups || [],
      lista: contentData?.lista || [],
    }));

    const fmt = parseMaybe(contentData?.conteudo_json)?.format;
    setFaqData(fmt === "faq.v1" ? sanitizeFaq(contentData.conteudo_json) : emptyFaq);
    setTermsData(fmt === "terms.v1" ? sanitizeTerms(contentData.conteudo_json) : emptyTerms);
    setLgpdData(fmt === "lgpd.v1" ? sanitizeLgpd(contentData.conteudo_json) : emptyLgpd);
    setAboutData(fmt === "about.v1" ? sanitizeAbout(contentData.conteudo_json) : emptyAbout);
    setHomeSeoData(fmt === "home_seo.v1" ? sanitizeHomeSeo(contentData.conteudo_json) : emptyHomeSeo);
    setTrainingKbData(
      fmt === "training_kb.v1"
        ? sanitizeTrainingKb(contentData.conteudo_json)
        : emptyTrainingKb()
    );
    setInstagramFeedData(
      fmt === "instagram_feed.v1"
        ? sanitizeInstagramFeed(contentData.conteudo_json)
        : emptyInstagramFeed
    );
    setMetaAccessToken("");
    setMetaPages([]);
    setMetaPagesError(null);
    setMetaPagesGuidance(null);
    setMetaPagesRequestId(null);
    setMetaOauthError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentData?.id]);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.provider !== "meta" || !data.accessToken) return;
      setMetaAccessToken(data.accessToken);
      setMetaOauthError(null);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (form.tipo !== "instagram_feed") {
      setMetaPages([]);
      setMetaPagesError(null);
      setMetaPagesGuidance(null);
      setMetaPagesRequestId(null);
      setLoadingMetaPages(false);
      return;
    }
    const tokenValue = metaAccessToken;
    if (!tokenValue) {
      setMetaPages([]);
      setMetaPagesError(null);
      setMetaPagesGuidance(null);
      setMetaPagesRequestId(null);
      setLoadingMetaPages(false);
      return;
    }

    let cancelled = false;
    const loadPages = async () => {
      setLoadingMetaPages(true);
      setMetaPagesError(null);
      setMetaPagesGuidance(null);
      setMetaPagesRequestId(null);
      try {
        const res = await getMetaPages({
          accessToken: tokenValue,
          includeInstagram: true,
        });
        if (cancelled) return;
        if (process.env.NODE_ENV === "development" && res?.requestId) {
          // eslint-disable-next-line no-console
          console.info("[Meta listMetaPages] requestId:", res.requestId);
        }
        const pages = Array.isArray(res?.pages) ? res.pages : [];
        setMetaPages(pages);
        setMetaPagesRequestId(res?.requestId || null);
        setMetaPagesGuidance(res?.guidance && typeof res.guidance === "object" ? res.guidance : null);
        const hasIg = (p) => !!(p.hasInstagram && p.instagramId);
        if (!pages.length) {
          if (!res?.guidance) {
            setMetaPagesError(
              "Conectamos à Meta, mas não encontramos páginas com Instagram Business vinculado."
            );
          } else {
            setMetaPagesError(null);
          }
        } else if (!pages.some(hasIg)) {
          setMetaPagesGuidance(null);
          setMetaPagesError(
            "Encontramos Páginas do Facebook, mas nenhuma possui Instagram Business vinculado. Associe o Instagram a uma Página ou ajuste as permissões."
          );
        } else {
          setMetaPagesError(null);
          setMetaPagesGuidance(null);
        }
      } catch (err) {
        if (cancelled) return;
        const apiBody = err?.data && typeof err.data === "object" ? err.data : null;
        const guidance = apiBody?.guidance;
        setMetaPagesRequestId(apiBody?.requestId || null);
        setMetaPagesGuidance(guidance && typeof guidance === "object" ? guidance : null);
        const msg =
          guidance?.title ||
          err?.message ||
          err?.response?.data?.message ||
          "Não foi possível listar as páginas da Meta. Tente conectar novamente e confira as permissões.";
        setMetaPages([]);
        setMetaPagesError(guidance ? null : msg);
      } finally {
        if (!cancelled) setLoadingMetaPages(false);
      }
    };

    loadPages();
    return () => {
      cancelled = true;
    };
  }, [form.tipo, metaAccessToken]);

  useEffect(() => {
    if (form.tipo !== "instagram_feed") return;
    const igId = instagramFeedData.instagram_business_account_id;
    if (!igId || !metaPages.length) return;
    const p = metaPages.find(
      (x) => x.instagramId && String(x.instagramId) === String(igId)
    );
    if (!p?.pageAccessToken) return;
    setInstagramFeedData((prev) => {
      if (prev.page_access_token === p.pageAccessToken) return prev;
      return {
        ...prev,
        page_access_token: p.pageAccessToken,
        facebook_page_id: String(p.pageId),
        ig_username: p.instagramUsername || prev.ig_username,
        page_name: p.pageName || prev.page_name,
      };
    });
  }, [form.tipo, metaPages, instagramFeedData.instagram_business_account_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "tipo") {
      const t = TYPES.find((x) => x.value === value);
      setForm((prev) => ({
        ...prev,
        tipo: value,
        slug: prev.slug || t?.defaultSlug || prev.slug,
      }));
      if (value === "instagram_feed" && form.tipo !== "instagram_feed") {
        if (!form.id) setInstagramFeedData(emptyInstagramFeed);
        setMetaAccessToken("");
        setMetaPages([]);
        setMetaPagesError(null);
      }
      if (value !== "instagram_feed" && form.tipo === "instagram_feed") {
        setMetaAccessToken("");
        setMetaPages([]);
      }
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateSlug = () => {
    if (!form.titulo) return;
    setForm((p) => ({ ...p, slug: slugify(p.titulo) }));
  };

  // helpers (links/listas simples)
  const addLink = () =>
    setForm((p) => ({
      ...p,
      links: [...(p.links || []), { rotulo: "", href: "", target: "_self" }],
    }));
  const setLink = (idx, patch) =>
    setForm((p) => {
      const a = [...(p.links || [])];
      a[idx] = { ...a[idx], ...patch };
      return { ...p, links: a };
    });
  const removeLink = (idx) =>
    setForm((p) => ({
      ...p,
      links: (p.links || []).filter((_, i) => i !== idx),
    }));

  const addItem = () =>
    setForm((p) => ({ ...p, lista: [...(p.lista || []), ""] }));
  const setItem = (idx, value) =>
    setForm((p) => {
      const a = [...(p.lista || [])];
      a[idx] = value;
      return { ...p, lista: a };
    });
  const removeItem = (idx) =>
    setForm((p) => ({
      ...p,
      lista: (p.lista || []).filter((_, i) => i !== idx),
    }));

  // Flags de tipo
  const isFaqStructured = form.tipo === "faq";
  const isTerms = form.tipo === "termos";
  const isLgpd = form.tipo === "lgpd";
  const isAbout = form.tipo === "sobre";
  const isHomeSeo = form.tipo === "home_seo";
  const isTraining = form.tipo === "treinamento_plataforma";
  const isInstagramFeed = form.tipo === "instagram_feed";

  const igPageOptions = useMemo(() => {
    const withIg = metaPages.filter((p) => p.hasInstagram && p.instagramId);
    const cur = instagramFeedData.facebook_page_id;
    if (
      cur &&
      !withIg.some((p) => String(p.pageId) === String(cur))
    ) {
      return [
        {
          pageId: cur,
          pageName: instagramFeedData.page_name || "Página salva",
          instagramId: instagramFeedData.instagram_business_account_id,
          instagramUsername: instagramFeedData.ig_username,
          pageAccessToken: instagramFeedData.page_access_token,
          hasInstagram: true,
          _saved: true,
        },
        ...withIg,
      ];
    }
    return withIg;
  }, [metaPages, instagramFeedData]);

  const handleConnectMetaInstagram = async () => {
    setMetaOauthError(null);
    try {
      setMetaConnecting(true);
      const url = await getMetaOauthUrl("instagram_messenger");
      if (!url) {
        setMetaOauthError(
          "Não foi possível obter a URL de autenticação da Meta."
        );
        return;
      }
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        url,
        "meta_oauth",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Não foi possível iniciar a autenticação com a Meta.";
      setMetaOauthError(msg);
    } finally {
      setMetaConnecting(false);
    }
  };

  const handleInstagramPageSelect = (e) => {
    const pageId = e.target.value;
    if (!pageId) {
      setInstagramFeedData((prev) => ({
        ...prev,
        facebook_page_id: "",
        instagram_business_account_id: "",
        page_access_token: "",
        ig_username: "",
        page_name: "",
      }));
      return;
    }
    const p = igPageOptions.find(
      (x) => String(x.pageId) === String(pageId)
    );
    if (!p?.instagramId) return;
    setInstagramFeedData((prev) => ({
      ...prev,
      facebook_page_id: String(p.pageId),
      instagram_business_account_id: String(p.instagramId),
      page_access_token: p.pageAccessToken || prev.page_access_token || "",
      ig_username: p.instagramUsername || "",
      page_name: p.pageName || "",
    }));
  };

  // Páginas com editor rico simples (um campo só)
  const isLongPage = [
    "como_comprar",
    "pagamento",
    "entregas",
    "trocas",
    "privacidade",
    "cookies",
  ].includes(form.tipo);

  const isLinks = form.tipo === "links_rodape";
  const isSimpleList = ["sugestoes_busca", "segmentos"].includes(form.tipo);

  const escapeHtml = (s = "") =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!form.titulo?.trim()) {
      setErrorMessage("Informe o título.");
      return;
    }

    const payload = {
      id: form.id,
      tipo: form.tipo,
      titulo: form.titulo.trim(),
      slug: (form.slug || slugify(form.titulo)).trim(),
      ordem: form.ordem === "" ? null : Math.trunc(Number(form.ordem)),
      status: Number(form.status) === 1 ? 1 : 0,
      updated_at: form.updated_at || "",
      auto_toc: !!form.auto_toc,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      // defaults
      conteudo: null,
      conteudo_json: null,
      links: null,
      faqs: null,
      faq_groups: null,
      lista: null,
    };

    if (isFaqStructured) {
      const normalized = sanitizeFaq(faqData);
      normalized.groups = (normalized.groups || [])
        .map((g) => ({
          ...g,
          items: (g.items || []).filter((it) => it.pergunta && it.resposta),
        }))
        .filter((g) => g.titulo && g.items.length);
      payload.conteudo_json = normalized;
      payload.auto_toc = false;
    } else if (isTerms) {
      const normalized = sanitizeTerms(termsData);
      normalized.secoes.conta.itens =
        (normalized.secoes.conta.itens || []).filter(Boolean);
      normalized.secoes.pagamentos.itens =
        (normalized.secoes.pagamentos.itens || []).filter(Boolean);
      normalized.secoes.trocas.itens =
        (normalized.secoes.trocas.itens || []).filter(Boolean);
      normalized.secoes.uso.itens =
        (normalized.secoes.uso.itens || []).filter(Boolean);
      normalized.secoes.gerais.itens =
        (normalized.secoes.gerais.itens || []).filter(Boolean);
      payload.conteudo_json = normalized;
      payload.auto_toc = false;
    } else if (isLgpd) {
      const normalized = sanitizeLgpd(lgpdData);
      normalized.bases_legais = (normalized.bases_legais || []).filter(Boolean);
      normalized.finalidades = (normalized.finalidades || []).filter(Boolean);
      normalized.direitos = (normalized.direitos || []).filter(Boolean);
      normalized.como_exercer_steps =
        (normalized.como_exercer_steps || []).filter(Boolean);
      normalized.contatos = (normalized.contatos || []).filter(
        (l) => l.label || l.href
      );
      payload.conteudo_json = normalized;
      payload.auto_toc = false;
    } else if (isHomeSeo) {
      const normalized = sanitizeHomeSeo(homeSeoData);

      const blocks = [];
      if (normalized.h1) {
        blocks.push(`<h1>${escapeHtml(normalized.h1)}</h1>`);
      }
      if (normalized.intro) {
        const paras = normalized.intro
          .split(/\n{2,}/)
          .map((p) => p.trim())
          .filter(Boolean)
          .map((p) => `<p>${escapeHtml(p)}</p>`);
        blocks.push(...paras);
      }
      normalized.sections.forEach((sec) => {
        if (!sec.h2 && !sec.body) return;
        if (sec.h2) {
          blocks.push(`<h2>${escapeHtml(sec.h2)}</h2>`);
        }
        if (sec.body) {
          const paras = sec.body
            .split(/\n{2,}/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((p) => `<p>${escapeHtml(p)}</p>`);
          blocks.push(...paras);
        }
      });

      payload.conteudo_json = normalized;
      payload.conteudo = blocks.join("\n\n");
      payload.auto_toc = false;
    } else if (isAbout) {
      const normalized = sanitizeAbout(aboutData);
      payload.conteudo_json = normalized;
      payload.auto_toc = false;
    } else if (isTraining) {
      const normalized = sanitizeTrainingKb(trainingKbData);
      if (!normalized.modules.length) {
        setErrorMessage("Adicione pelo menos um módulo com título ou materiais.");
        return;
      }
      payload.conteudo_json = normalized;
      payload.auto_toc = false;
    } else if (isInstagramFeed) {
      const normalized = sanitizeInstagramFeed(instagramFeedData);
      const prevTok = String(
        parseMaybe(contentData?.conteudo_json)?.page_access_token || ""
      ).trim();
      const token =
        normalized.page_access_token ||
        (form.id && prevTok ? prevTok : "");
      const merged = { ...normalized, page_access_token: token };
      if (!merged.instagram_business_account_id || !merged.page_access_token) {
        setErrorMessage(
          "Conecte à Meta e selecione uma página com Instagram Business vinculado."
        );
        return;
      }
      payload.conteudo_json = merged;
      payload.auto_toc = false;
    } else {
      // páginas simples
      if (isLongPage) payload.conteudo = form.conteudo || "";
      if (isLinks) payload.links = form.links || [];
      if (isSimpleList) payload.lista = form.lista || [];
    }

    try {
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Erro ao salvar o conteúdo."
      );
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Conteúdo" : "Adicionar Conteúdo"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                name="tipo"
                label="Tipo"
                value={form.tipo}
                onChange={handleChange}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <TextField
              label="Título *"
              name="titulo"
              value={form.titulo}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Ordem"
              name="ordem"
              value={form.ordem ?? ""}
              onChange={handleChange}
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={9} sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Slug"
              name="slug"
              value={form.slug}
              onChange={handleChange}
              fullWidth
              placeholder="ex.: sobre"
            />
            <Button
              variant="outlined"
              onClick={handleGenerateSlug}
              sx={{ whiteSpace: "nowrap" }}
            >
              Gerar slug
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Última atualização"
              name="updated_at"
              type="date"
              value={form.updated_at || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* TOC só para páginas simples; estruturadas não usam */}
          {/* {!isFaqStructured && !isTerms && !isLgpd && !isAbout && (
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.auto_toc}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        auto_toc: e.target.checked,
                      }))
                    }
                  />
                }
                label="Gerar índice (TOC) a partir de H2/H3"
              />
            </Grid>
          )} */}

          {/* Editores estruturados */}
          {isFaqStructured && (
            <Grid item xs={12}>
              <FaqEditor value={faqData} onChange={setFaqData} />
            </Grid>
          )}
          {isTerms && (
            <Grid item xs={12}>
              <TermsEditor value={termsData} onChange={setTermsData} />
            </Grid>
          )}
          {isLgpd && (
            <Grid item xs={12}>
              <LgpdEditor value={lgpdData} onChange={setLgpdData} />
            </Grid>
          )}
          {isAbout && (
            <Grid item xs={12}>
              <AboutEditor value={aboutData} onChange={setAboutData} />
            </Grid>
          )}
          {isTraining && (
            <Grid item xs={12}>
              <TrainingKbEditor value={trainingKbData} onChange={setTrainingKbData} />
            </Grid>
          )}

          {isInstagramFeed && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Instagram — últimas publicações
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Conecte à Meta (mesmo fluxo do canal Instagram), escolha a página cujo
                perfil profissional está vinculado. O token fica guardado de forma segura;
                o site público recebe só as mídias via API.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleConnectMetaInstagram}
                  disabled={metaConnecting || isLoading}
                >
                  {metaConnecting ? "Conectando..." : "Conectar com Meta"}
                </Button>
              </Box>
              {metaOauthError && (
                <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                  {metaOauthError}
                </Typography>
              )}
              {(metaPagesError || metaPagesGuidance) && (
                <Box sx={{ mb: 1.5 }}>
                  {metaPagesGuidance ? (
                    <MetaPagesGuidanceBlock guidance={metaPagesGuidance} requestId={metaPagesRequestId} />
                  ) : (
                    <>
                      <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
                        {metaPagesError}
                      </Typography>
                      <List dense sx={{ pl: 2 }}>
                        <ListItem sx={{ py: 0.25, display: "list-item" }}>
                          <Typography variant="caption" color="text.secondary">
                            Confirme que você é administrador da Página e que o Instagram é
                            profissional e vinculado à Página.{" "}
                            <Link
                              href="https://www.facebook.com/business/help/898752960195806"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Ajuda da Meta
                            </Link>
                          </Typography>
                        </ListItem>
                      </List>
                    </>
                  )}
                </Box>
              )}
              <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoading}>
                <InputLabel>Página / Instagram</InputLabel>
                <Select
                  label="Página / Instagram"
                  value={instagramFeedData.facebook_page_id || ""}
                  onChange={handleInstagramPageSelect}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) {
                      return loadingMetaPages
                        ? "Carregando páginas..."
                        : "Selecione após conectar à Meta";
                    }
                    const opt = igPageOptions.find(
                      (p) => String(p.pageId) === String(v)
                    );
                    if (!opt) return v;
                    const un = opt.instagramUsername
                      ? `@${opt.instagramUsername}`
                      : "";
                    return `${opt.pageName || "Página"}${un ? ` — ${un}` : ""}`;
                  }}
                >
                  <MenuItem value="">
                    <em>Nenhuma</em>
                  </MenuItem>
                  {igPageOptions.map((p) => (
                    <MenuItem key={String(p.pageId)} value={String(p.pageId)}>
                      {p.pageName || "Página"}
                      {p.instagramUsername
                        ? ` — @${p.instagramUsername}`
                        : ""}
                      {p._saved ? " (salva)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {loadingMetaPages && metaAccessToken && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="caption" color="text.secondary">
                    Buscando páginas…
                  </Typography>
                </Box>
              )}
              {instagramFeedData.ig_username &&
                !metaAccessToken &&
                !loadingMetaPages && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Conta configurada: @{instagramFeedData.ig_username}. Use &quot;Conectar com
                    Meta&quot; para trocar de página.
                  </Typography>
                )}
              <TextField
                fullWidth
                label="Texto de apoio (opcional)"
                value={instagramFeedData.lead}
                onChange={(e) =>
                  setInstagramFeedData((p) => ({ ...p, lead: e.target.value }))
                }
                multiline
                minRows={2}
                placeholder="Ex.: Bastidores e o dia a da frota — como no nosso Instagram."
                helperText="Aparece no site como subtítulo da seção, se o tema usar este campo."
              />
            </Grid>
          )}

          {/* Campos específicos para Texto SEO da home */}
          {isHomeSeo && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Título principal (H1)
                </Typography>
                <TextField
                  fullWidth
                  value={homeSeoData.h1}
                  onChange={(e) =>
                    setHomeSeoData((p) => ({ ...p, h1: e.target.value }))
                  }
                  placeholder="Ex.: Peças para Caminhões Scania: linha completa de reposição"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Introdução (parágrafos iniciais)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={homeSeoData.intro}
                  onChange={(e) =>
                    setHomeSeoData((p) => ({ ...p, intro: e.target.value }))
                  }
                  placeholder="Texto introdutório da home. Use quebras de linha duplas para separar parágrafos."
                />
              </Grid>

              {homeSeoData.sections.map((sec, idx) => (
                <Grid item xs={12} key={idx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Seção {idx + 1} (H2 + texto)
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      fullWidth
                      label="Subtítulo (H2)"
                      value={sec.h2}
                      onChange={(e) =>
                        setHomeSeoData((p) => {
                          const next = { ...p };
                          next.sections = [...next.sections];
                          next.sections[idx] = {
                            ...next.sections[idx],
                            h2: e.target.value,
                          };
                          return next;
                        })
                      }
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Texto da seção"
                      value={sec.body}
                      onChange={(e) =>
                        setHomeSeoData((p) => {
                          const next = { ...p };
                          next.sections = [...next.sections];
                          next.sections[idx] = {
                            ...next.sections[idx],
                            body: e.target.value,
                          };
                          return next;
                        })
                      }
                      placeholder="Texto desta seção. Use quebras de linha duplas para separar parágrafos."
                    />
                  </Box>
                </Grid>
              ))}
            </>
          )}

          {/* Páginas com editor rico (Word-like) */}
          {isLongPage && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Conteúdo
              </Typography>
              <RichTextEditor
                value={form.conteudo || ""}
                onChange={(html) =>
                  setForm((prev) => ({ ...prev, conteudo: html }))
                }
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Você pode colar texto formatado (ex.: Word) e ajustar
                negrito, itálico, títulos, listas e links diretamente aqui.
              </Typography>
            </Grid>
          )}

          {/* Links de rodapé */}
          {isLinks && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Links
              </Typography>
              {form.links?.map((l, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 3fr 1fr auto",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    label="Rótulo"
                    value={l.rotulo}
                    onChange={(e) =>
                      setLink(idx, { rotulo: e.target.value })
                    }
                  />
                  <TextField
                    label="URL"
                    value={l.href}
                    onChange={(e) =>
                      setLink(idx, { href: e.target.value })
                    }
                  />
                  <FormControl>
                    <InputLabel>Target</InputLabel>
                    <Select
                      label="Target"
                      value={l.target || "_self"}
                      onChange={(e) =>
                        setLink(idx, { target: e.target.value })
                      }
                    >
                      <MenuItem value="_self">_self</MenuItem>
                      <MenuItem value="_blank">_blank</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => removeLink(idx)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              ))}
              <Button startIcon={<AddRoundedIcon />} onClick={addLink}>
                Adicionar link
              </Button>
            </Grid>
          )}

          {/* Listas simples */}
          {isSimpleList && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {form.tipo === "sugestoes_busca"
                  ? "Sugestões"
                  : "Segmentos"}
              </Typography>
              {form.lista?.map((v, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    value={v}
                    onChange={(e) => setItem(idx, e.target.value)}
                    fullWidth
                  />
                  <IconButton onClick={() => removeItem(idx)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Box>
              ))}
              <Button startIcon={<AddRoundedIcon />} onClick={addItem}>
                Adicionar item
              </Button>
            </Grid>
          )}
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Salvar"
            )}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddContentForm;
